import { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from './useAuth';
import { checkUserAccess } from '../services/accessControl';
import { PRICING } from '../config/pricing';

export interface ReactivationInsights {
  hasLapsedAccess: boolean;
  totalNewFlows: number;
  totalUpdatedFlows: number;
  allNewFlows: Array<{
    name: string;
    developerName: string;
    type: string;
    lastModified: string;
    orgName?: string;
  }>;
  allUpdatedFlows: Array<{
    name: string;
    developerName: string;
    type: string;
    oldVersion: number | null;
    newVersion: number | null;
    lastModified: string;
    orgName?: string;
  }>;
  byOrg: Record<string, {
    orgName: string;
    newFlows: number;
    updatedFlows: number;
  }>;
  estimatedHours: number;
  estimatedCost: number;
  monthlySubscriptionPrice: number;
  savings: number;
  accessLostDate: Date | null;
  accessLostReason: string | null;
  daysLapsed: number;
  hasConnectionIssues: boolean;
  connectionIssueOrgs: Array<{
    orgId: string;
    orgName: string;
  }>;
  loading: boolean;
  lastChecked: Date | null;
}

export function useReactivationInsights() {
  const { user } = useAuth();
  const [insights, setInsights] = useState<ReactivationInsights>({
    hasLapsedAccess: false,
    totalNewFlows: 0,
    totalUpdatedFlows: 0,
    allNewFlows: [],
    allUpdatedFlows: [],
    byOrg: {},
    estimatedHours: 0,
    estimatedCost: 0,
    monthlySubscriptionPrice: PRICING.STARTER.MONTHLY, // Default until we fetch actual plan
    savings: 0,
    accessLostDate: null,
    accessLostReason: null,
    daysLapsed: 0,
    hasConnectionIssues: false,
    connectionIssueOrgs: [],
    loading: true,
    lastChecked: null
  });

  useEffect(() => {
    if (!user) {
      setInsights(prev => ({ ...prev, loading: false }));
      return;
    }

    const fetchInsights = async () => {
      try {
        // Check if user has lapsed access
        const accessCheck = await checkUserAccess(user.id);
        const hasLapsedAccess = !accessCheck.hasAccess;

        if (!hasLapsedAccess) {
          // User has access - no need to show reactivation insights
          setInsights(prev => ({ ...prev, hasLapsedAccess: false, loading: false }));
          return;
        }

        // Get account with subscription plan
        const { data: account } = await supabase
          .from('accounts')
          .select('id, subscription_plan')
          .eq('owner_id', user.id)
          .single();

        if (!account) {
          setInsights(prev => ({ ...prev, loading: false }));
          return;
        }

        // Get connected orgs count for fallback pricing logic
        const { count: orgCount } = await supabase
          .from('sf_orgs')
          .select('id', { count: 'exact', head: true })
          .eq('account_id', account.id);

        // Determine the appropriate price based on previous plan or org count
        const getMonthlyPrice = (): number => {
          const plan = account.subscription_plan?.toLowerCase();

          // If we have a previous plan, use that pricing
          if (plan === 'starter') {
            return PRICING.STARTER.MONTHLY;
          } else if (plan === 'standard') {
            return PRICING.STANDARD.MONTHLY;
          } else if (plan === 'professional' || plan === 'pro') {
            return PRICING.PROFESSIONAL.MONTHLY;
          }

          // Fallback: base on org count (1 org = Starter, >1 = Professional)
          if (orgCount && orgCount > 1) {
            return PRICING.PROFESSIONAL.MONTHLY;
          }
          return PRICING.STARTER.MONTHLY;
        };

        const monthlyPrice = getMonthlyPrice();

        // Get existing insights from database
        const { data: dbInsights, error: insightsError } = await supabase
          .from('account_reactivation_insights')
          .select(`
            *,
            sf_orgs (
              id,
              name
            )
          `)
          .eq('account_id', account.id);

        if (insightsError) {
          console.error('Error fetching insights:', insightsError);
          setInsights(prev => ({ ...prev, loading: false }));
          return;
        }

        // Check if we need to trigger a drift check
        const now = new Date();
        const shouldCheckDrift = !dbInsights || dbInsights.length === 0 ||
          dbInsights.some(insight => {
            const lastChecked = new Date(insight.last_checked_at);
            const hoursSinceCheck = (now.getTime() - lastChecked.getTime()) / (1000 * 60 * 60);
            return hoursSinceCheck > 24; // Check if last check was > 24 hours ago
          });

        if (shouldCheckDrift) {
          // Trigger drift detection in background
          fetch('/api/reactivation/detect-drift', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ accountId: account.id })
          }).catch(err => console.error('Background drift check failed:', err));
        }

        if (!dbInsights || dbInsights.length === 0) {
          // No insights yet - show loading state
          setInsights(prev => ({
            ...prev,
            hasLapsedAccess: true,
            loading: true
          }));
          return;
        }

        // Process insights
        let totalNew = 0;
        let totalUpdated = 0;
        const allNew: any[] = [];
        const allUpdated: any[] = [];
        const byOrg: Record<string, any> = {};
        let totalHours = 0;
        let totalCost = 0;
        let connectionIssues: any[] = [];
        let latestAccessLostDate: Date | null = null;
        let latestAccessLostReason: string | null = null;
        let maxDaysLapsed = 0;
        let latestCheck: Date | null = null;

        dbInsights.forEach(insight => {
          const orgName = insight.sf_orgs?.name || 'Unknown Org';

          // Check for connection issues
          if (insight.status === 'connection_broken') {
            connectionIssues.push({
              orgId: insight.org_id,
              orgName
            });
            return; // Skip processing this org
          }

          totalNew += insight.new_flows_count || 0;
          totalUpdated += insight.updated_flows_count || 0;
          totalHours += parseFloat(insight.estimated_hours_saved || '0');
          totalCost += parseFloat(insight.estimated_cost_impact || '0');

          // Add org-specific breakdown
          byOrg[insight.org_id] = {
            orgName,
            newFlows: insight.new_flows_count || 0,
            updatedFlows: insight.updated_flows_count || 0
          };

          // Combine all new flows
          (insight.new_flows_details || []).forEach((flow: any) => {
            allNew.push({ ...flow, orgName });
          });

          // Combine all updated flows
          (insight.updated_flows_details || []).forEach((flow: any) => {
            allUpdated.push({ ...flow, orgName });
          });

          // Track access lost info
          if (insight.access_lost_date) {
            const lostDate = new Date(insight.access_lost_date);
            if (!latestAccessLostDate || lostDate > latestAccessLostDate) {
              latestAccessLostDate = lostDate;
              latestAccessLostReason = insight.access_lost_reason;
            }
          }

          if (insight.days_since_access_lost > maxDaysLapsed) {
            maxDaysLapsed = insight.days_since_access_lost;
          }

          // Track latest check time
          const checkedAt = new Date(insight.last_checked_at);
          if (!latestCheck || checkedAt > latestCheck) {
            latestCheck = checkedAt;
          }
        });

        // Calculate savings (cost of manual work - subscription price)
        const savings = totalCost - monthlyPrice;

        setInsights({
          hasLapsedAccess: true,
          totalNewFlows: totalNew,
          totalUpdatedFlows: totalUpdated,
          allNewFlows: allNew,
          allUpdatedFlows: allUpdated,
          byOrg,
          estimatedHours: Math.round(totalHours * 10) / 10,
          estimatedCost: Math.round(totalCost * 100) / 100,
          monthlySubscriptionPrice: monthlyPrice,
          savings: Math.round(savings * 100) / 100,
          accessLostDate: latestAccessLostDate,
          accessLostReason: latestAccessLostReason,
          daysLapsed: maxDaysLapsed,
          hasConnectionIssues: connectionIssues.length > 0,
          connectionIssueOrgs: connectionIssues,
          loading: false,
          lastChecked: latestCheck
        });

      } catch (error) {
        console.error('Error fetching reactivation insights:', error);
        setInsights(prev => ({ ...prev, loading: false }));
      }
    };

    fetchInsights();

    // Subscribe to insights changes
    const channel = supabase
      .channel('reactivation-insights')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'account_reactivation_insights',
        },
        () => {
          fetchInsights();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  return insights;
}
